import React from 'react';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSearch} from '@fortawesome/free-solid-svg-icons';

import {PaginationScroll} from 'Components/Partials';
import {Select} from 'Components/Form';
import {PrimaryButton} from 'Components/Button';
import {ModalTrigger} from 'Components/Modal';

import {
    ProfilePic,
    ActiveUsersService,
    handleMessagePreview,
    isSameChat,
    isSameChatCheck,
    CreateGroupModal
} from 'Components/Networking/Sub';

import UsersApi from 'Services/Api/Networking/Users';
import GroupsApi from 'Services/Api/Networking/Groups';
import ToApi from 'Services/Api/Networking/To';

import {Event} from 'Services';
import {translation} from 'Services/TranslationHelpers';
import {mergeObjectsIntoPaginationData} from 'Services/BaseHelpers';

export default class Users extends React.Component {
    /**
     * @method constructor
     */
    constructor() {
        super();
        this.selectedChatRef = React.createRef();
    }

    /**
     * @var state
     */
    state = {
        activeUsers: [],
        activeUserIds: [],
        usersOrGroups: 'users',
        users: null,
        key: true
    }

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        const {selected_chat} = this.props;

        this.handleActiveUsers();

        if (selected_chat) {
            this.handleSelectedChat(selected_chat);
        }

        GroupsApi.on('updated', this.refreshUsers);
    }

    /**
     * @method componentDidUpdate
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const {selected_chat} = this.props;

        if (selected_chat && prevProps.selected_chat !== selected_chat) {
            this.scrollToSelectedChat();

            this.handleSelectedChat(selected_chat);
        }
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        GroupsApi.removeListener('updated', this.refreshUsers);
    };

    /**
     * @method refreshUsers
     */
    refreshUsers = () => {
        const {key} = this.state;

        this.setState({
            key: !key
        });
    }

    /**
     * @method handleSelectedChat
     * @param {object} selected_chat
     */
    handleSelectedChat = (selected_chat) => {
        /*
        TODO
        PaginationScroll resets the data when loading the first page therefore we have to wait
        until after that page has loaded to add this chat. Used a hacky setTimeout solution.
         */
        setTimeout(() => {
            this.handleNewChat(selected_chat);
        }, 500);
    }

    /**
     * @method handleActiveUsers
     */
    handleActiveUsers = () => {
        this.setState({
            activeUsers: ActiveUsersService.getActiveUsers(),
            activeUserIds: ActiveUsersService.getActiveUserIds()
        });

        Event.on('networking-active-users', () => {
            this.setState({
                activeUsers: ActiveUsersService.getActiveUsers(),
                activeUserIds: ActiveUsersService.getActiveUserIds()
            });
        });
    }

    /**
     * @method handleNewChat
     * @param {object} chat
     * @return {Promise<void>}
     */
    handleNewChat = (chat) => {
        const {usersOrGroups, users} = this.state;

        let newUsersOrGroups = chat.type === 'App\\Models\\User' ? 'users' : 'groups';

        // If chat is not yet in the list then add it.
        // If changing usersOrGroups then reset users to empty.
        if (users.data.filter(o => isSameChat(o, chat)).length === 0) {
            return this.setState({
                users: usersOrGroups === newUsersOrGroups ?
                    mergeObjectsIntoPaginationData(users, [chat], false, isSameChatCheck()) :
                    null,
                usersOrGroups: newUsersOrGroups
            });
        }
    }

    /**
     * @method scrollToSelectedChat
     */
    scrollToSelectedChat = () => {
        if (this.selectedChatRef.current !== null) {
            this.selectedChatRef.current.scrollIntoView({behavior: 'smooth'});
        }
    }

    /**
     * @method getApiCall
     * @param {boolean} useData
     * @return {object}
     */
    getApiCall = (useData) => {
        const {usersOrGroups} = this.state;

        if (useData) {
            if (usersOrGroups === 'users') {
                return (data) => UsersApi.get(null, data);
            } else if (usersOrGroups === 'groups') {
                return (data) => GroupsApi.get(null, data);
            }
        } else {
            if (usersOrGroups === 'users') {
                return (page, search) => UsersApi.get(null, {page, search});
            } else if (usersOrGroups === 'groups') {
                return (page, search) => GroupsApi.get(null, {page, search});
            }
        }
    }

    /**
     * @method setSearchSelectedChat
     * @param {string} id
     */
    setSearchSelectedChat = async (id) => {
        const {usersOrGroups} = this.state;
        const {setSelectedChat} = this.props;

        const response = await ToApi.get(id, {
            message_to_type: usersOrGroups === 'users' ? 'App\\Models\\User' : 'App\\Models\\NetworkingGroup'
        });

        setSelectedChat(response.data.data);
    }

    /**
     * @method sortUsers
     * @param {object} user_a
     * @param {object} user_b
     * @return {integer}
     */
    sortUsers = (user_a, user_b) => {
        const {activeUserIds} = this.state;

        // If both active users then leave as is.
        if (user_a.type === "App\\Models\\User" && activeUserIds.indexOf(user_a.id) > -1
            && user_b.type === "App\\Models\\User" && activeUserIds.indexOf(user_b.id) > -1) {
            return 1;
        }

        // Put active users at the top.
        if (user_a.type === "App\\Models\\User" && activeUserIds.indexOf(user_a.id) > -1) {
            return -1;
        }

        return 1;
    }

    /**
     * @method render
     * @return {*}
     */
    render() {
        const {useGroups} = this.props;
        const {usersOrGroups} = this.state;

        return (
            <div className="h-full flex flex-col px-2 border-r-0 md:border-r-2 -mt-2 border-app-blue max-h-[65em] overflow-hidden">
                <h1 className="h1 mx-auto lg:mx-1 my-4 text-lg text-app-blue ml-3 mt-5">
                    Contacts
                </h1>

                {/*{useGroups && this.renderUsersOrGroupsToggle()}*/}

                {this.renderSearchBar()}

                {window.base.features.users_can_create_networking_groups &&
                    usersOrGroups === 'groups' &&
                    this.renderGroupCreateButton()}

                {this.renderUsers()}
            </div>
        )
    }

    /**
     * @method renderUsersOrGroupsToggle
     * @return {*}
     */
    renderUsersOrGroupsToggle() {
        const {usersOrGroups} = this.state;

        let options = ['users', 'groups'];

        return (
            <div className="grid grid-cols-2 text-center  cursor-pointer mb-4">
                {options.map((option, i) => (
                    <div
                        className={`p-4 ${usersOrGroups === option ? 'bg-red-400 text-white' : ''}`}
                        onClick={() => {
                            if (usersOrGroups !== option) {
                                this.setState({
                                    usersOrGroups: option,
                                    users: null
                                })
                            }
                        }}
                        key={i}
                    >
                        {translation('networking_frontend', option)}
                    </div>
                ))}
            </div>
        );
    }

    /**
     * @method renderSearchBar
     * @return {*}
     */
    renderSearchBar() {
        const {setSelectedChat} = this.props;
        const {usersOrGroups} = this.state;

        let apiCall = this.getApiCall(true);

        return (
            <div className="w-full px-2 mb-4">
                <div className="p-2 bg-white">
                    <Select
                        containerClassName="w-full bg-app-lighter-blue"
                        onChange={(v) => this.setSearchSelectedChat(v)}
                        searchCallback={apiCall}
                        searchLabelKey="name"
                        placeholder={translation('networking_frontend', 'search_name')}
                        isAsync
                        icon={faSearch}
                        key={usersOrGroups}
                        className="border-none"
                        colouredOptions={true}
                    />
                </div>
            </div>
        );
    }

    /**
     * @method renderGroupCreateButton
     * @return {*}
     */
    renderGroupCreateButton() {
        return (
            <div className="mb-4 mx-auto">
                <ModalTrigger
                    component={CreateGroupModal}
                >
                    <PrimaryButton
                        text="Create New Group"
                    />
                </ModalTrigger>
            </div>
        );
    }

    /**
     * @method renderUsers
     * @return {*}
     */
    renderUsers() {
        const {activeUsers, usersOrGroups, users, key} = this.state;

        let apiCall = this.getApiCall(false);

        // Add the activeUsers to the users displayed.
        if (usersOrGroups === 'users') {
            var allUsers = mergeObjectsIntoPaginationData(users, activeUsers, false, isSameChatCheck());
        } else if (usersOrGroups === 'groups') {
            var allUsers = users;
        }

        return (
            <PaginationScroll
                scrollDown={true}
                apiCall={apiCall}
                updateData={(data) => this.setState({users: data})}
                data={users}
                containerClassName="px-2"
                key={usersOrGroups + key}
                sameObjectCheck={isSameChatCheck()}
            >
                {allUsers && allUsers.data.length === 0 &&
                    <div className="p-4 font-bold text-center text-app-blue">
                        {translation('networking_frontend', 'check_back')}
                    </div>
                }

                {allUsers && allUsers.data.sort((a, b) => {
                    return this.sortUsers(a, b)
                }).map((user, i) => (this.renderUser(user)))}
            </PaginationScroll>
        );
    }

    /**
     * @method renderUser
     * @param {object} user
     * @return {*}
     */
    renderUser(user) {
        const {activeUserIds} = this.state;
        const {selected_chat, setSelectedChat} = this.props;

        let is_selected_chat = (selected_chat && isSameChat(selected_chat, user));

        return (
            <div
                className={`
                    mt-1 mb-1 first:mt-0 last:mb-0 p-4
                    relative cursor-pointer overflow-visible
                    border-b-2 border-b-app-gray
                    flex flex-col xl:flex-row justify-start items-center gap-4
                    ${is_selected_chat ?
                        'bg-app-gray text-networking-primary-text' :
                        'bg-transparent'}
                    ${(activeUserIds.indexOf(user.id) > -1 ?
                        '' :
                        '')}
                    hover:bg-app-gray hover:text-networking-primary-text
                `}
                onClick={() => setSelectedChat(user)}
                ref={is_selected_chat ? this.selectedChatRef : null}
                key={user.type + '_' + user.id}
            >
                <ProfilePic user={user}/>

                <div>
                    <p className="font-bold text-center xl:text-left text-networking-secondary-text">
                        { user.name }
                    </p>

                    <p className="font-thin text-center xl:text-left text-networking-secondary-text">
                        { user.company_name }
                    </p>

                    <p className="font-thin text-center xl:text-left text-networking-secondary-text">
                        { user.job_title }
                    </p>

                    { user.attendance_type && user.attendance_type !== '' &&
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${user.attendance_type === 'Virtual' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            { user.attendance_type }
                        </span>
                    }

                    { user.subtitle && user.subtitle !== '' &&
                        <p className="text-center xl:text-left text-networking-secondary-text text-sm">
                            { user.subtitle }
                        </p>
                    }
                </div>
            </div>
        );
    }
}
